import React, { useEffect } from "react";
import { Controller } from "react-hook-form";

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import SelectField from "../../ui/components/SelectField/SelectField";
import InputField from "../../ui/components/InputField/InputField";
import useCustomerContext from "../../customers/hooks/useCustomerContext";
import {useOidcClientContext} from "../../ui/context/OidcClientContext";

const isDNIValid = (value: string, t: Function, documentType: string) => {
    const dniRegex = /^(\d{8})([A-Z])$/i;
    const match = value.match(dniRegex);
    if (!match) return t("booking.invalidDocumentTypeFormat", {"documentType" : documentType});

    const number = parseInt(match[1], 10);
    const letter = match[2].toUpperCase();
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    const expectedLetter = validLetters[number % 23];

    if (letter !== expectedLetter) return t("booking.invalidDocumentTypeLetter", {"documentType" : documentType});  // Usar la traducción
    return true;
};

const isNIEValid = (value: string, t: Function) => {
    const nieRegex = /^[XYZ]\d{7}[A-Z]$/i;
    const match = value.match(nieRegex);
    if (!match) return t("booking.invalidNIEFormat");  // Usar la traducción

    const letter = value.slice(-1).toUpperCase();
    let number = value.slice(0, -1).toUpperCase();
    const prefix = { X: "0", Y: "1", Z: "2" };
    number = prefix[number[0]] + number.slice(1);
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    const expectedLetter = validLetters[parseInt(number, 10) % 23];

    if (letter !== expectedLetter) return t("booking.invalidNIELetter");  // Usar la traducción
    return true;
};

const BookingNationalInputs = ({ t, register, errors, watch, setValue, profile, control }) => {
    const {authenticated} = useOidcClientContext();
    const {languageCode} = useCustomerContext();
    countries.registerLocale(enLocale);
    useEffect(() => {
        countries.registerLocale(require(`i18n-iso-countries/langs/${languageCode}.json`));
    }, [languageCode]);

    const nationality = watch("nationality");
    const documentType = watch("documentType");

    const documentOptions = React.useMemo(() => {
        return nationality === "ES"
            ? [
                { value: "DNI", label: "DNI" },
                {value: 'NIF', label: "NIF"},
            ]
            : [
                { value: "NIE", label: "NIE" },
                { value: "PAS", label: t("booking.passport") },
                {value: 'VAT', label: "VAT"},
                {value: 'OTR', label: t('booking.other')},
            ];
    }, [nationality, t]);

    useEffect(() => {
        if (authenticated && profile) {
            setValue("nationality", profile?.nationality);
            setValue("documentType", profile?.documentType);
            setValue("invoicePassportCif", profile?.documentNumber);
        }
    }, [authenticated, profile, setValue]);

    useEffect(() => {
        if (!authenticated || !profile) {
            setValue("documentType", "");
            setValue("invoicePassportCif", "");
        }
    }, [nationality, authenticated, profile, setValue]);

    return (
        <div className="lg:flex lg:space-x-5">
            <div className="lg:w-1/3 mb-2">
                <Controller
                    name="nationality"
                    control={control}
                    rules={{
                        required: t("common.feedback.field_required"),
                    }}
                    render={({ field }) => (
                        <SelectField
                            label={t("booking.nationality")}
                            error={errors.nationality?.message}
                            {...field}
                        >
                            <option value="">{t("")}</option>
                            {Object.entries(countries.getNames(languageCode))
                                .map(([code, name]) => ({ code, name: name as string }))
                                .sort((a, b) => a.name.localeCompare(b.name)).map(({ code, name }) => (
                                <option key={code} value={code}>
                                    {name}
                                </option>
                            ))}
                        </SelectField>
                    )}
                />
            </div>
            <div className="lg:w-1/3 mb-2">
                <SelectField
                    label={t("booking.documentType")}
                    error={errors.documentType?.message}
                    {...register("documentType", {
                        required: t("common.feedback.field_required"),
                    })}
                >
                    <option value="">{t("")}</option>
                    {documentOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </SelectField>
            </div>
            <div className="lg:w-1/3 mb-2">
                <InputField
                    label={t("booking.invoicePassportCif")}
                    defaultValue=""
                    {...register("invoicePassportCif", {
                        required: t("common.feedback.field_required"),
                        validate:
                            documentType === "DNI" || documentType === "NIF" ? (value: string) => isDNIValid(value, t, documentType)
                                : documentType === "NIE" ? (value: string) => isNIEValid(value, t) : undefined,
                    })}
                    error={errors.invoicePassportCif?.message}
                />
            </div>
        </div>
    );
};

export default BookingNationalInputs;
